import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * RadioGroup - Modern radio button group component
 * Provides single-choice selection with better UX than default radio buttons
 */
const RadioGroup = ({ 
  options = [], 
  value, 
  onChange, 
  name, 
  required = false, 
  disabled = false,
  orientation = 'vertical', // 'vertical' or 'horizontal'
  variant = 'default', // 'default' | 'card'
  className = '',
  ...props 
}) => {
  const { theme } = useTheme();

  const handleChange = (optionValue) => {
    if (disabled) return;
    onChange && onChange(optionValue);
  };

  const containerClass = `
    ${orientation === 'horizontal' ? 'flex flex-row gap-4' : 'flex flex-col gap-2'}
    ${className}
  `.trim();

  return (
    <div className={containerClass} role="radiogroup" {...props}>
      {options.map((option, index) => {
        const optionValue = typeof option === 'string' ? option : option.value;
        const optionLabel = typeof option === 'string' ? option : option.label;
        const optionDisabled = disabled || (option.disabled);
        const isSelected = value === optionValue;
        const radioId = `${name}-${index}`;

        return (
          <label
            key={optionValue}
            htmlFor={radioId}
            className={`
              flex items-center gap-3 transition-all duration-200
              ${
                variant === 'card'
                  ? `rounded-xl border px-4 py-3 ${
                      isSelected
                        ? 'bg-blue-50/80 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900/50 text-blue-600 dark:text-blue-400'
                        : `border-gray-200 dark:border-gray-700 ${theme.text.primary} ${
                            optionDisabled
                              ? 'opacity-50 cursor-not-allowed'
                              : 'cursor-pointer hover:bg-slate-50/80 dark:hover:bg-slate-900/30'
                          }`
                    }`
                  : `${optionDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'} ${
                      isSelected ? 'text-blue-600 dark:text-blue-400' : theme.text.primary
                    }`
              }
            `}
          >
            <div className="relative">
              <input
                id={radioId}
                type="radio"
                name={name}
                value={optionValue}
                checked={isSelected}
                onChange={() => handleChange(optionValue)}
                required={required}
                disabled={optionDisabled}
                className="sr-only" // Hide default radio button
              />
              
              {/* Custom Radio Button */}
              <div
                className={`
                  w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center
                  ${isSelected 
                    ? 'border-blue-500 bg-blue-500 dark:border-blue-400 dark:bg-blue-400' 
                    : `border-gray-300 dark:border-gray-600 ${theme.colors.surface}`
                  }
                  ${!optionDisabled && 'hover:border-blue-400 dark:hover:border-blue-500'}
                  ${optionDisabled ? 'opacity-50' : 'focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-opacity-50'}
                `}
              >
                {/* Inner Circle */}
                {isSelected && (
                  <div className="w-2 h-2 rounded-full bg-white animate-in zoom-in-50 duration-200" />
                )}
              </div>
            </div>
            
            {/* Label */}
            <span className={`text-sm font-medium ${optionDisabled ? 'opacity-50' : ''}`}>
              {optionLabel}
            </span>
          </label>
        );
      })}
    </div>
  );
};

/**
 * RadioGroupItem - Individual radio item (for more granular control)
 */
const RadioGroupItem = ({ 
  value, 
  children, 
  disabled = false, 
  className = '',
  ...props 
}) => {
  const { theme } = useTheme();

  return (
    <div className={`flex items-center gap-3 ${className}`} {...props}>
      <div
        className={`
          w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center
          border-gray-300 dark:border-gray-600 ${theme.colors.surface}
          ${!disabled && 'hover:border-blue-400 dark:hover:border-blue-500'}
          ${disabled ? 'opacity-50' : 'focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-opacity-50'}
        `}
      >
        <input
          type="radio"
          value={value}
          disabled={disabled}
          className="sr-only"
          {...props}
        />
      </div>
      {children && (
        <span className={`text-sm font-medium ${disabled ? 'opacity-50' : theme.text.primary}`}>
          {children}
        </span>
      )}
    </div>
  );
};

export { RadioGroup, RadioGroupItem };
export default RadioGroup;
